// Soft keys: follow-up questions suggested under the latest answer, built from the topic and
// budget year of the question. Public visitors only get years they can open.
const FIRST_YEAR = 2020;
const LAST_YEAR = 2026;
const LAST_PUBLIC_YEAR = 2022;

const TOPICS = [
  { key: "highlights", re: /highlight|overview|key (points|announcements)|tell me about/i, label: "Key highlights", ask: (fy) => `What are the key highlights of the Union Budget ${fy}?`, compare: null },
  { key: "deficit", re: /fiscal deficit|deficit/i, label: "Fiscal deficit", ask: (fy) => `What is the fiscal deficit in ${fy}?`, compare: (a, b) => `How has the fiscal deficit changed from ${a} to ${b}?` },
  { key: "capex", re: /capital expenditure|capex|infrastructure/i, label: "Capital expenditure", ask: (fy) => `What is the capital expenditure in ${fy}?`, compare: (a, b) => `Compare capital expenditure from ${a} to ${b}.` },
  { key: "tax", re: /\btax|gst|customs|duty|duties/i, label: "Tax changes", ask: (fy) => `What were the tax changes in the ${fy} budget?`, compare: null },
  { key: "defence", re: /defen[cs]e|military|armed forces/i, label: "Defence budget", ask: (fy) => `What is the defence allocation in ${fy}?`, compare: (a, b) => `Compare the defence budget from ${a} to ${b}.` },
  { key: "agriculture", re: /agricultur|farm|kisan/i, label: "Agriculture", ask: (fy) => `What did the ${fy} budget announce for agriculture?`, compare: null },
  { key: "health", re: /health|medical|hospital/i, label: "Health", ask: (fy) => `What did the ${fy} budget announce for health?`, compare: null },
  { key: "receipts", re: /receipts|revenue|borrowing/i, label: "Receipts", ask: (fy) => `What are the total receipts in ${fy}?`, compare: null },
];
// Topics suggested for a year when the question was about something else, in this order.
const RELATED = ["highlights", "deficit", "capex", "tax"];

// 2022 -> "2022–⁠23" for labels (word joiner keeps it on one line) and "2022-23" for prompts.
const fyLabel = (year) => `${year}–⁠${String(year + 1).slice(2)}`;
const fyPrompt = (year) => `${year}-${String(year + 1).slice(2)}`;

// Every budget year (by its starting year) mentioned in the text: "2022-23", "FY23" or "2022".
export function budgetYears(text) {
  const value = String(text || "");
  const years = [
    ...[...value.matchAll(/\b(20[2-3]\d)\b/g)].map((m) => Number(m[1])),
    ...[...value.matchAll(/\bFY\s*'?(\d{2})\b/gi)].map((m) => 2000 + Number(m[1]) - 1),
  ];
  return years.filter((year) => year >= FIRST_YEAR && year <= LAST_YEAR);
}

const topicOf = (text) => TOPICS.find((topic) => topic.re.test(text)) || null;

export function followUps(question, answer, registered) {
  const lastYear = registered ? LAST_YEAR : LAST_PUBLIC_YEAR;
  const asked = budgetYears(question);
  // For a trend question ("since 2020-21") the answer's latest year is the one to build on.
  const seen = /since|change|trend|compare|over the years/i.test(question) ? [...asked, ...budgetYears(answer)] : asked.length ? asked : budgetYears(answer);
  const year = seen.length ? Math.min(Math.max(...seen), lastYear) : lastYear;
  const topic = topicOf(question);
  const keys = [];
  const add = (label, prompt) => {
    if (keys.length < 3 && prompt.trim().toLowerCase() !== question.trim().toLowerCase() && !keys.some((k) => k.prompt === prompt)) keys.push({ label, prompt });
  };

  if (topic) {
    const other = year > FIRST_YEAR ? year - 1 : year + 1;
    if (other <= lastYear) add(`${topic.label} · ${fyLabel(other)}`, topic.ask(fyPrompt(other)));
    if (topic.compare && year > FIRST_YEAR) add(`Trend ${fyLabel(FIRST_YEAR)} to ${fyLabel(year)}`, topic.compare(fyPrompt(FIRST_YEAR), fyPrompt(year)));
  }
  RELATED.filter((key) => key !== topic?.key).forEach((key) => {
    const related = TOPICS.find((t) => t.key === key);
    add(`${related.label} · ${fyLabel(year)}`, related.ask(fyPrompt(year)));
  });

  return { keys, locked: !registered && (asked.some((y) => y > lastYear) || /log ?in/i.test(answer)) };
}
